import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { withRouter } from 'core/libs/router';

import { limit } from 'core/queries/searchPage';

import pluralize from 'core/utils/pluralize';

import H3 from 'core/components/H3';
import Feed from 'core/components/Feed';
import Block from 'core/components/GridBlock';
import ListPage from 'core/components/ListPage';
import AdWrapper from 'core/components/Ad/AdWrapper';

import searchResultsPropTypes from 'core/utils/prop-types/search';
import modelPropTypes, { topicAttributes } from 'core/utils/prop-types/model';

import styles from './index.styl';


const DefaultFoundText = ({ total }) =>
  (
    <div className={styles.total}>
      <H3>{total} {pluralize(total, [
        'материал',
        'материала',
        'материалов',
      ])}</H3>
    </div>
  );

DefaultFoundText.propTypes = {
  total: PropTypes.number,
};

/**
 * @author Alexey Rezakov <a.rezakov@rambler-co.ru>
 */
function SearchData({
  rawContent,
  ad1: Ad1,
  ad2: Ad2,
  ad1Mobile: Ad1Mobile,
  ad2Mobile: Ad2Mobile,
  interitemSpacing,
  card,
  pagination,
  foundTextBlock: FoundTextBlock,
  isLoading,
}) {
  const feedProps = {
    card,
    interitemSpacing,
  };
  const total = rawContent?.meta?.search_results?.total || 0;

  const Found = FoundTextBlock || DefaultFoundText;

  return (
    <AdWrapper bottom={interitemSpacing}>
      <Found total={total} />
      <ListPage
        rawData={rawContent}
        limit={limit}
        pagination={pagination}
        isLoading={isLoading}
      >
        {({ content }) => {
          const first = content.slice(0, 5);
          const second = content.slice(5, 10);
          const third = content.slice(10, 15);
          return (
            <Fragment>
              <div style={{ marginBottom: interitemSpacing }}>
                <Feed content={first}
                  dataQa='search-data-first'
                  {...feedProps}
                />
              </div>

              {Ad1 && <Block mobile={0}><Ad1 /></Block>}
              {Ad1Mobile && <Block desktop={0}><Ad1Mobile /></Block>}

              {second.length > 0 && (
                <Fragment>
                  <div style={{ marginBottom: interitemSpacing }}>
                    <Feed content={second}
                      dataQa='search-data-second'
                      {...feedProps}
                    />
                  </div>
                  {Ad2 && <Block mobile={0}><Ad2 /></Block>}
                  {Ad2Mobile && <Block desktop={0}><Ad2Mobile /></Block>}
                </Fragment>
              )}

              {third.length > 0 && (
                <div style={{ marginBottom: interitemSpacing }}>
                  <Feed content={third}
                    dataQa='search-data-third'
                    {...feedProps}
                  />
                </div>
              )}
            </Fragment>
          );
        }}
      </ListPage>
    </AdWrapper>
  );
}

SearchData.propTypes = {
  /**
   * Компонент кастомного текста про найденные материалы
   */
  foundTextBlock: PropTypes.func,

  /**
   * Компонент кастомной паджинации
   */
  pagination: PropTypes.func,

  /** @ignore */
  history: PropTypes.object,

  /** @ignore */
  location: PropTypes.object,

  /**
   * Карточка для поиска.
   *
   * В `relationships.search_result` можно получить поля `searchTitle` и `searchBody`.<br/>
   * Таким образом найденная фраза может быть выделена тегом `<b>`.
   *
   * Посмотреть пример карточки можно здесь DemoSearchCard
   */
  card: PropTypes.func.isRequired,
  /**
   * Реклама после 5 элемента
   */
  ad1: PropTypes.func,

  /**
   * Реклама после 10 элемента
   */
  ad2: PropTypes.func,

  /**
   * Мобильная реклама после 5 элемента
   */
  ad1Mobile: PropTypes.func,

  /**
   * Мобильная реклама после 10 элемента
   */
  ad2Mobile: PropTypes.func,

  /**
   * Отступ между карточками
   */
  interitemSpacing: PropTypes.number,

  /**
   * Резульат запроса от ручки `getSearch`
   */
  rawContent: PropTypes.shape({
    topics: PropTypes.arrayOf(modelPropTypes(topicAttributes).isRequired),
    meta: PropTypes.shape({
      search_results: searchResultsPropTypes.isRequired,
    }).isRequired,
  }),

  /**
   * Обязательное свойство ListPage
   */
  isLoading: PropTypes.bool,
};

const SearchDataWithHOCs = withRouter(SearchData);
SearchDataWithHOCs.displayName = 'SearchData';

export { SearchData as StorybookComponent };
export default SearchDataWithHOCs;
